// User Configuration Service
// Centralized service for managing user preferences and formatting settings

import { getAssetMetadata, AssetType } from '@/shared/constants/asset-precision'
import {
  formatNumberWithConfig,
  formatNumberWithFixedDecimalsUsingConfig,
  type DecimalStringRoundingMode,
} from '@/shared/services/user-config.number-format'
import type { UserConfiguration } from '@/shared/types/services.types'
import { STORAGE_KEYS } from '@/shared/utils/constants'
import { logger } from '@/shared/utils/logger'
import { instantFromWallClock, wallClockInZone } from '@/shared/utils/zoned-time'

// DEFAULT_USER_CURRENCY is the canonical default currency for new users and
// for the local fallback before the backend `/profile/preferences` payload
// arrives. Backend persists USD (see
// packages/backend/src/platform/auth/constants/auth.constants.ts
// DEFAULT_USER_PREFERENCES.currency = 'USD'); frontend must match so the UI
// does not flicker EUR → USD on first paint.
const DEFAULT_USER_CURRENCY = 'USD'

// Browser-detected timezone (e.g. `'Europe/Madrid'`, `'America/New_York'`,
// `'Asia/Tokyo'`) chosen lazily so the singleton picks the actual user's IANA
// zone instead of leaking a Spanish default to every tenant (issue #358).
// Falls back to `'UTC'` on environments that do not implement Intl
// resolvedOptions (e.g. very old test runners). DO NOT hardcode 'Europe/Madrid'
// here — tax timestamp math (cost-basis dates, holding-period thresholds) must
// align with where the user actually lives.
const detectBrowserTimezone = (): string => {
  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone
    return detected && typeof detected === 'string' ? detected : 'UTC'
  } catch {
    return 'UTC'
  }
}

const DEFAULT_CONFIG: UserConfiguration = {
  dateFormat: 'DD.MM.YYYY',
  timeFormat: '24h',
  numberFormat: 'european',
  timezone: detectBrowserTimezone(),
  currency: DEFAULT_USER_CURRENCY,
  baseCurrency: DEFAULT_USER_CURRENCY,
  language: 'es',
  theme: 'dark',
  compactMode: false,
  animationsEnabled: true,
  // defaultPageSize = 25 is the user-preference seed value, persisted via
  // localStorage and surfaced through Settings. It deliberately sits between
  // PAGINATION_DEFAULTS.MEDIUM (20) and LARGE (50) and is the canonical literal
  // that other "25" page-size sites mirror — keep as a literal here.
  defaultPageSize: 25,
  autoSave: true,
  notifications: true,
  maxDecimals: 8,
  minDecimals: 2,
  thousandsSeparator: '.',
  decimalSeparator: ',',
}

class UserConfigService {
  private config: UserConfiguration
  private listeners: Array<(config: UserConfiguration) => void> = []
  private storageKey = STORAGE_KEYS.USER_CONFIG

  constructor() {
    this.config = this.loadConfig()
  }

  private loadConfig(): UserConfiguration {
    try {
      const stored = localStorage.getItem(this.storageKey)
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<UserConfiguration> & {
          theme?: UserConfiguration['theme'] | 'auto'
        }
        // Legacy alias: old builds could persist theme 'auto' (removed
        // THEME_OPTIONS entry) meaning "follow the system". Normalize to
        // 'system' — the only value the theme engine understands for that.
        // String() because the declared union no longer contains 'auto';
        // only legacy localStorage payloads can still hold it.
        if (String(parsed.theme) === 'auto') {
          parsed.theme = 'system'
        }
        return { ...DEFAULT_CONFIG, ...parsed }
      }
    } catch (error) {
      logger.warn('Failed to load user config from localStorage:', error)
    }
    return { ...DEFAULT_CONFIG }
  }

  private saveConfig(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.config))
      this.notifyListeners()
    } catch (error) {
      logger.error('Failed to save user config to localStorage:', error)
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener(this.config)
      } catch (error) {
        logger.error('Error in config listener:', error)
      }
    })
  }

  // Config Getters
  getConfig(): UserConfiguration {
    return { ...this.config }
  }

  // Typed as the shared vocabulary, not `string`: callers that decide how to READ a typed
  // date (the matching date filter) must be forced to handle exactly the four tokens the
  // backend accepts — a widened `string` is how a display format goes unchecked.
  getDateFormat(): UserConfiguration['dateFormat'] {
    return this.config.dateFormat
  }

  getTimeFormat(): string {
    return this.config.timeFormat
  }

  getNumberFormat(): string {
    return this.config.numberFormat
  }

  getTheme(): string {
    return this.config.theme
  }

  // Config Setters
  updateConfig(updates: Partial<UserConfiguration>): void {
    this.config = { ...this.config, ...updates }
    this.saveConfig()
  }

  setDateFormat(format: UserConfiguration['dateFormat']): void {
    this.updateConfig({ dateFormat: format })
  }

  setNumberFormat(format: UserConfiguration['numberFormat']): void {
    this.updateConfig({ numberFormat: format })
  }

  setTheme(theme: UserConfiguration['theme']): void {
    this.updateConfig({ theme })
  }

  // Reset configuration
  resetToDefaults(): void {
    this.config = { ...DEFAULT_CONFIG }
    this.saveConfig()
  }

  // Event listeners
  subscribe(listener: (config: UserConfiguration) => void): () => void {
    this.listeners.push(listener)

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener)
      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    }
  }

  // Formatting Methods
  formatDate(
    date: Date | string,
    includeTime = false,
    timeZone: string = this.config.timezone,
    dateFormat: UserConfiguration['dateFormat'] = this.config.dateFormat,
  ): string {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date

      if (isNaN(dateObj.getTime())) {
        return ''
      }

      // The wall clock of the instant in the PROFILE zone (not the browser's): the same stored moment reads
      // 08:30 in Tokyo and 18:30 the day before in New York.
      const clock = wallClockInZone(dateObj, timeZone)
      if (!clock) return ''
      const year = clock.year.toString()
      const month = clock.month.toString().padStart(2, '0')
      const day = clock.day.toString().padStart(2, '0')

      // Format date according to user preference
      let formatted = ''
      switch (dateFormat) {
        case 'MM/DD/YYYY':
          formatted = `${month}/${day}/${year}`
          break
        case 'YYYY-MM-DD':
          formatted = `${year}-${month}-${day}`
          break
        case 'DD/MM/YYYY':
          formatted = `${day}/${month}/${year}`
          break
        case 'DD.MM.YYYY':
        default:
          formatted = `${day}.${month}.${year}`
          break
      }

      if (includeTime) {
        const hours = clock.hours.toString().padStart(2, '0')
        const minutes = clock.minutes.toString().padStart(2, '0')
        const seconds = clock.seconds.toString().padStart(2, '0')

        // Always use 24-hour format for consistency
        formatted += ` ${hours}:${minutes}:${seconds}`
      }

      return formatted
    } catch (error) {
      logger.error('Error formatting date:', error, date)
      return ''
    }
  }

  // Render a clock time honoring the user's timeFormat preference: '24h' → "23:35",
  // '12h' → "11:35 PM". Reads the wall clock in the PROFILE zone (like formatDate) and never
  // toLocaleTimeString, so the output is deterministic and preference-driven rather than
  // runtime-locale-driven.
  formatTime(date: Date | string, options?: { seconds?: boolean }): string {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date
      if (isNaN(dateObj.getTime())) return ''

      const clock = wallClockInZone(dateObj, this.config.timezone)
      if (!clock) return ''
      const h24 = clock.hours
      const minutes = clock.minutes.toString().padStart(2, '0')
      // `{ seconds: true }` writes «h:mm:ss» in the same 12/24 h branch (the refresh notices of the admin panel).
      const minutesAndSeconds = options?.seconds
        ? `${minutes}:${clock.seconds.toString().padStart(2, '0')}`
        : minutes

      if (this.config.timeFormat === '12h') {
        const period = h24 >= 12 ? 'PM' : 'AM'
        const h12 = h24 % 12 === 0 ? 12 : h24 % 12
        return `${h12}:${minutesAndSeconds} ${period}`
      }

      return `${h24.toString().padStart(2, '0')}:${minutesAndSeconds}`
    } catch (error) {
      logger.error('Error formatting time:', error, date)
      return ''
    }
  }

  formatNumber(
    value: number | string | null | undefined,
    asset?: string,
    options?: {
      allowCompactAbbreviation?: boolean
      knownType?: AssetType
      // Los dos de abajo viajan intactos al formateador puro: son el contrato de
      // «no redondees un decimal guardado» (#2211), no una opción de estilo.
      displayDecimalsOverride?: number
      roundingMode?: DecimalStringRoundingMode
    },
  ): string {
    // Asset precision is resolved HERE (this singleton owns the getAssetMetadata
    // dependency); the pure formatter receives the resolved metadata plus
    // this.config so it stays free of `this`/I/O. See user-config.number-format.
    // `knownType` matters for the LONG TAIL: `classifyAssetType` only recognises a
    // hardcoded list of major tickers, so every other crypto fell back to the
    // UNKNOWN default of 6 decimals. Callers that already know the type (the API
    // sends `asset.type` on every leg) must not be forced through that guess.
    const metadata = getAssetMetadata(asset, options?.knownType)
    return formatNumberWithConfig(value, metadata, this.config, options)
  }

  /**
   * Format with EXACTLY `fixedDecimals` decimals using the separators of the
   * active numberFormat preference. For amount columns that must always render
   * a fixed precision (e.g. spec 4.2.2: 8 decimals for crypto amounts on
   * /balances) while still honoring the user's separator preference.
   * Degenerate input follows the formatNumber contract: null/undefined → '',
   * unparseable values are echoed back as-is.
   */
  formatNumberWithFixedDecimals(
    value: number | string | null | undefined,
    fixedDecimals: number,
  ): string {
    return formatNumberWithFixedDecimalsUsingConfig(value, fixedDecimals, this.config)
  }

  // Currency formatting
  formatCurrency(amount: number, currency?: string): string {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string currency means unset and must fall back to the base currency
    const curr = currency || this.config.baseCurrency
    // Pass the currency AS the asset: without it `getAssetMetadata(undefined)`
    // returns DEFAULT_METADATA (6 decimals — the conservative fallback for an
    // UNKNOWN ticker) and a euro rendered as "658,494918 EUR" in the Traza
    // VALOR column. `AssetType.FIAT` resolves to 2 decimals, 0 for the
    // zero-decimal tickers (JPY/KRW), and keeps trailing cents. A crypto code
    // passed here still classifies as crypto, so satoshi-level amounts are not
    // flattened to 2dp.
    const formatted = this.formatNumber(amount, curr)

    // Add currency symbol/code based on preference
    return `${formatted} ${curr}`
  }

  // Asset formatting (for crypto assets)
  formatAsset(amount: number, asset: string): string {
    // For crypto assets, always show more decimals and no thousands separator for precision
    const maxDecimals = asset.includes('BTC') ? 8 : 6
    return `${amount.toFixed(maxDecimals).replace(/\.?0+$/, '')} ${asset}`
  }

  // Timezone utilities
  getUserTimezone(): string {
    return this.config.timezone
  }

  /**
   * Parse a date typed in the configured display format (what `formatDate`
   * writes) back into the instant it denotes.
   *
   * The wall clock is read in the same zone `formatDate` renders it in — the
   * PROFILE zone — so `parseFromDisplayFormat(formatDate(d, true))` is `d`
   * for whichever zone the profile names.
   * @param displayValue - Value in display format (DD.MM.YYYY HH:mm:ss)
   * @returns The instant (serialise with `toISOString()` for the backend) or null if invalid
   */
  parseFromDisplayFormat(displayValue: string): Date | null {
    if (!displayValue || typeof displayValue !== 'string') return null

    // Parse according to the CONFIGURED format. `formatDate` emits four
    // (MM/DD/YYYY, YYYY-MM-DD, DD/MM/YYYY, DD.MM.YYYY) and this used to accept
    // only the dot one, so three of them failed here and the caller
    // (useInlineEdit) fell through to `new Date(value)` — which V8 reads as US
    // MM/DD/YYYY. Browser-proven: a user on DD/MM/YYYY typed 05/09/2026
    // (5 September) into a transaction date and 2026-05-09 (9 MAY) was stored,
    // silently; 25/09/2026 became Invalid Date and the edit vanished with no
    // message. A parser must be able to read its own formatter's output.
    const trimmed = displayValue.trim()
    const format = this.config.dateFormat

    const separator = format.includes('.') ? '\\.' : format.includes('-') ? '-' : '/'
    const datePattern =
      format === 'YYYY-MM-DD'
        ? `(?<year>\\d{4})${separator}(?<month>\\d{2})${separator}(?<day>\\d{2})`
        : format === 'MM/DD/YYYY'
          ? `(?<month>\\d{2})${separator}(?<day>\\d{2})${separator}(?<year>\\d{4})`
          : `(?<day>\\d{2})${separator}(?<month>\\d{2})${separator}(?<year>\\d{4})`

    // The time part is optional — `formatDate(date, true)` appends it, plain
    // `formatDate(date)` does not, and both must round-trip.
    const match = new RegExp(
      `^${datePattern}(?:\\s+(?<hours>\\d{2}):(?<minutes>\\d{2}):(?<seconds>\\d{2}))?$`,
    ).exec(trimmed)
    if (!match?.groups) return null

    const { year, month, day, hours, minutes, seconds } = match.groups
    const yearNumber = Number(year)
    const monthNumber = Number(month)
    const dayNumber = Number(day)
    const hoursNumber = Number(hours ?? 0)
    const minutesNumber = Number(minutes ?? 0)
    const secondsNumber = Number(seconds ?? 0)

    // Neither `10:75:00` nor 31/02 exists: refuse it instead of storing a different moment.
    // (`instantFromWallClock` judges the calendar day and the clock time on their own.)
    return instantFromWallClock(
      {
        year: yearNumber,
        month: monthNumber,
        day: dayNumber,
        hours: hoursNumber,
        minutes: minutesNumber,
        seconds: secondsNumber,
      },
      this.config.timezone,
    )
  }
}

// Singleton instance
export const userConfigService = new UserConfigService()

// UserConfiguration is already exported as interface above
